import { Stack } from "@mui/material";
import React, { useState } from "react";

import { cn } from "../utils/styling";
import { DividerVertical } from "./Divider";
import { Icon, type IconProps } from "./Icon";
import { IconButton } from "./IconButton";

export type LDSToastVariant = "error" | "info" | "neutral" | "positive";

const VARIANT_STYLES: Record<LDSToastVariant, { iconColor: string; iconName: IconProps["iconName"]; root: string }> = {
  error: {
    iconColor: "var(--core-color-mystic-red-800)",
    iconName: "Warning",
    root: "border-core-mystic-red-300",
  },
  info: {
    iconColor: "var(--core-color-secondary-blue-500)",
    iconName: "Info",
    root: "border-core-secondary-blue-400",
  },
  neutral: {
    iconColor: "var(--core-color-grey-800)",
    iconName: "Sun",
    root: "border-core-grey-300",
  },
  positive: {
    iconColor: "var(--core-color-plunge-pool-800)",
    iconName: "Checkmark",
    root: "border-core-plunge-pool-300",
  },
};

type LDSToastProps = {
  className?: string;
  invisibleOnDismiss?: boolean;
  message: string;
  onDismiss?: () => void;
  onUndo?: () => void;
  showDismiss?: boolean;
  showIcon?: boolean;
  showUndo?: boolean;
  variant?: LDSToastVariant;
};

export const LDSToast = ({
  className,
  invisibleOnDismiss = false,
  message,
  onDismiss,
  showDismiss = true,
  showIcon = true,
  variant = "info",
}: LDSToastProps) => {
  const hasActions = showDismiss;
  const styles = VARIANT_STYLES[variant];

  const [open, setOpen] = useState(true);

  return (
    <Stack
      className={cn(
        "min-h-14 w-full gap-2 rounded-sm border border-solid bg-lds-surface-bright px-4 py-2",
        styles.root,
        !open && invisibleOnDismiss && "invisible",
        !open && !invisibleOnDismiss && "hidden",
        className
      )}
      justifyContent="space-between"
    >
      <Stack className="flex-1 gap-1">
        {showIcon ? (
          <Stack className="h-10 w-10 justify-center">
            <Icon color={styles.iconColor} iconName={styles.iconName} size="md" />
          </Stack>
        ) : null}
        <Stack className="min-h-0 min-w-0 flex-1">
          <p className="font-semibold text-lds-foreground-strong font-ds-body-regular-xxs">{message}</p>
        </Stack>
      </Stack>
      {hasActions && (
        <Stack spacing={2}>
          <DividerVertical className="h-10" />
          <IconButton
            buttonProps={{
              onClick: () => {
                setOpen(false);
                onDismiss?.();
              },
              variant: "tertiary",
            }}
            iconProps={{
              color: "var(--color-foreground-secondary)",
              iconName: "Close",
              size: "sm",
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
